// @ts-strict-ignore
import {AfterViewInit, Component, DoCheck, ElementRef, Input, IterableDiffer, IterableDiffers, output, ViewChild} from "@angular/core";
import {OccupationState, PlayerMapping, PlayerStatus, RoomCapability, TrainingRoom, TrainingRoomGroup, TrainingRoomType} from "@models";
import {SimStreamService, SimSystemService, SimTrainingRoomService, SimUploadService, SimUtilityService} from "@services";
import {HttpEventType} from "@angular/common/http";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import konva from "konva";
import {Translations} from "@translations";

const MID_MOUSE_BUTTON = 4;
const BUTTON_WIDTH = 180;
const BUTTON_HEIGHT = 36;
const ZOOM_STEP = 0.025;
const SCALE_BY = 1.025;
const SIMSTATION_COLOR = "#279989";
const SIMSTATION_OCCUPIED_COLOR = "#cc0000";

// 0 and 1 have to be in the array, otherwise the drag event will not be fired
konva.dragButtons = [0, 1, MID_MOUSE_BUTTON];

@Component({
	selector: "sim-training-room-plan-view",
	templateUrl: "./training-room-plan-view.component.html",
	styleUrls: ["./training-room-plan-view.component.scss"],
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class TrainingRoomPlanViewComponent implements AfterViewInit, DoCheck {
	T = Translations;

	@Input({required: true}) trainingRoomGroup: TrainingRoomGroup & {
		trainingRooms?: (TrainingRoom & {selectedPlayer?: PlayerMapping})[];
	};
	@Input({required: true}) trainingRooms: TrainingRoom[];
	@Input({required: true}) trainingRoomGroupIndex: number;
	@Input({required: true}) selectedTrainingRooms: TrainingRoom[];
	@Input() readOnly: boolean = false;

	onTrainingRoomClicked = output<{
		trainingRoom: TrainingRoom;
		checked: boolean;
	}>();

	PlayerStatus = PlayerStatus;
	TrainingRoomType = TrainingRoomType;
	RoomCapability = RoomCapability;

	private _stage?: konva.Stage;
	private _layer?: konva.Layer;
	private _backgroundLayer?: konva.Layer;

	private _selectedTrainingRooms = new Map<string, TrainingRoom>();
	private _occupiedTrainingRooms = new Map<string, TrainingRoom>();
	private _differ: IterableDiffer<TrainingRoom>;
	private _canvasSize = {width: 0, height: 0};

	@ViewChild("canvasContainer") canvasContainer: ElementRef<HTMLDivElement>;

	constructor(
		private _simUploadService: SimUploadService,
		private _simTrainingRoomService: SimTrainingRoomService,
		private _simSystemService: SimSystemService,
		private _simStreamService: SimStreamService,
		private _differs: IterableDiffers,
	) {
		this._differ = this._differs.find([]).create();
	}

	public ngAfterViewInit() {
		this.initCanvas();
	}

	ngDoCheck() {
		const changes = this._differ.diff(this.selectedTrainingRooms) || this._differ.diff(this.trainingRooms);
		if (changes) {
			this.onSelectedTrainingRoomsChanged();
		}
	}

	onSelectedTrainingRoomsChanged() {
		this._selectedTrainingRooms.clear();
		this._occupiedTrainingRooms.clear();
		for (const trainingRoom of this.selectedTrainingRooms) {
			this._selectedTrainingRooms.set(trainingRoom.id, trainingRoom);
		}

		for (const trainingRoom of this.trainingRooms) {
			if (trainingRoom.occupiedTrainingRoom?.occupationState === OccupationState.Occupied) {
				this._occupiedTrainingRooms.set(trainingRoom.id, trainingRoom);
			}
		}

		// we need to wait for the next tick, otherwise the canvas will not be initialized
		setTimeout(() => {
			for (const group of this._layer.find<konva.Group>("Group")) {
				const rect = group.findOne<konva.Rect>("Rect");
				const text = group.findOne<konva.Text>("Text");
				if (this._occupiedTrainingRooms.has(group.id())) {
					rect.fill(SIMSTATION_OCCUPIED_COLOR);
					text.fill("white");
					rect.stroke(SIMSTATION_OCCUPIED_COLOR);
				} else if (this._selectedTrainingRooms.has(group.id())) {
					rect.fill("white");
					text.fill(SIMSTATION_COLOR);
					rect.stroke(SIMSTATION_COLOR);
				} else {
					rect.fill(SIMSTATION_COLOR);
					text.fill("white");
					rect.stroke(SIMSTATION_COLOR);
				}
			}
		}, 0);
	}

	private initCanvas() {
		this._canvasSize = {
			width: this.canvasContainer.nativeElement.clientWidth,
			height: this.canvasContainer.nativeElement.clientHeight,
		};

		this._stage = new konva.Stage({
			container: "canvasContainer" + this.trainingRoomGroupIndex,
			draggable: true,
			...this._canvasSize,
		});
		this._backgroundLayer = new konva.Layer({
			listening: false,
		});
		this._layer = new konva.Layer();

		this._stage.add(this._backgroundLayer);
		this._stage.add(this._layer);
		this.addPanningControls();

		if (this.readOnly) {
			this.addZoomControls();
		}

		this.loadBackgroundImage(this.trainingRoomGroup);
		this.addTrainingRoomsToCanvas(this.trainingRoomGroup);
	}

	private addTrainingRoomsToCanvas(trainingRoomGroup: typeof this.trainingRoomGroup) {
		for (const trainingRoom of trainingRoomGroup.trainingRooms) {
			const group = new konva.Group({
				id: trainingRoom.id,
				x: trainingRoom.positionX ?? 0,
				y: trainingRoom.positionY ?? 0,
				draggable: !this.readOnly,
				width: BUTTON_WIDTH,
				height: BUTTON_HEIGHT,
			});

			const rect = new konva.Rect({
				fill: SIMSTATION_COLOR,
				width: BUTTON_WIDTH,
				height: BUTTON_HEIGHT,
				strokeWidth: 2,
				stroke: SIMSTATION_COLOR,
				cornerRadius: 4,
			});

			const hoverRect = new konva.Rect({
				fill: "white",
				width: BUTTON_WIDTH,
				height: BUTTON_HEIGHT,
				cornerRadius: 4,
				opacity: 0,
			});

			const text = new konva.Text({
				text: trainingRoom.name,
				width: BUTTON_WIDTH - 20,
				height: BUTTON_HEIGHT,
				offsetX: -10,
				fontFamily: "Roboto",
				fontSize: 15,
				fontStyle: "500",
				fill: "white",
				align: "center",
				verticalAlign: "middle",
				wrap: "none",
				ellipsis: true,
			});

			group.add(rect);
			group.add(hoverRect);
			group.add(text);

			group.on("mouseenter", (e) => {
				this.canvasContainer.nativeElement.style.cursor = "pointer";
				hoverRect.opacity(0.08);
			});

			group.on("mouseleave", () => {
				this.canvasContainer.nativeElement.style.cursor = "default";
				hoverRect.opacity(0);
			});

			group.on("click", (e) => {
				const id = group.id();
				const trainingRoom = this.trainingRoomGroup.trainingRooms.find((room) => room.id === id);
				const checked = this._selectedTrainingRooms.has(id);

				this.onTrainingRoomClicked.emit({
					trainingRoom: trainingRoom,
					checked: checked,
				});
			});

			group.on("dragstart", (e) => {
				this._backgroundLayer.opacity(0.75);
			});

			group.on("dragend", (e) => {
				this._backgroundLayer.opacity(1);
				trainingRoom.positionX = Math.round(e.target.x());
				trainingRoom.positionY = Math.round(e.target.y());

				this._simTrainingRoomService.updateTrainingRoom({trainingRoom, updateLayouts: false}).catch((err) => {
					console.log(err);
				});
			});

			group.on("dragmove", () => {
				// we restrict the movement by setting the position of the group to the max/min allowed position
				// this is done to prevent the user from dragging the group out of the viewport

				const minAllowedX = -this._stage.x();
				const minAllowedY = -this._stage.y();
				const maxAllowedX = this._canvasSize.width - this._stage.x() - BUTTON_WIDTH;
				const maxAllowedY = this._canvasSize.height - this._stage.y() - BUTTON_HEIGHT;

				if (group.x() < minAllowedX) {
					group.x(minAllowedX);
				}
				if (group.y() < minAllowedY) {
					group.y(minAllowedY);
				}

				if (group.x() > maxAllowedX) {
					group.x(maxAllowedX);
				}
				if (group.y() > maxAllowedY) {
					group.y(maxAllowedY);
				}
			});

			this._layer.add(group);
		}
		this._stage.draw();
	}

	private addPanningControls() {
		this._stage.off("dragstart");
		this._stage.on("dragstart", (e) => {
			this.canvasContainer.nativeElement.style.cursor = "grabbing";

			if (e.evt.buttons !== MID_MOUSE_BUTTON && e.target !== this._stage) {
				// event was bubbling from a child. intent to drag child, ignore
				return;
			}

			if (e.evt.buttons !== MID_MOUSE_BUTTON && e.target === this._stage) {
				// stage can only be dragged with middle mouse button, ignore
				e.target.stopDrag();
				return;
			}

			if (e.evt.buttons === MID_MOUSE_BUTTON && e.target !== this._stage) {
				// event was bubbling from a child. intent to drag stage, stop drag and start drag on stage
				e.target.stopDrag();
				this._stage.startDrag();
				return;
			}
		});

		this._stage.off("dragend");
		this._stage.on("dragend", () => {
			this.canvasContainer.nativeElement.style.cursor = "default";
		});
	}

	private addZoomControls() {
		this._stage.on("wheel", (e) => {
			// stop default scrolling
			e.evt.preventDefault();

			const pointer = this._stage.getPointerPosition();
			const oldScale = this._stage.scaleX();

			const mousePointTo = {
				x: (pointer.x - this._stage.x()) / this._stage.scaleX(),
				y: (pointer.y - this._stage.y()) / this._stage.scaleY(),
			};

			// how to scale? Zoom in? Or zoom out?
			let direction = e.evt.deltaY > 0 ? 1 : -1;

			// when we zoom on trackpad, e.evt.ctrlKey is true
			// in that case lets revert direction
			if (e.evt.ctrlKey) {
				direction *= -1;
			}

			var newScale = direction > 0 ? oldScale * SCALE_BY : oldScale / SCALE_BY;

			this._stage.scale({x: newScale, y: newScale});

			var newPos = {
				x: pointer.x - mousePointTo.x * newScale,
				y: pointer.y - mousePointTo.y * newScale,
			};
			this._stage.position(newPos);
		});
	}

	async zoomInBackground(trainingRoomGroup: typeof this.trainingRoomGroup) {
		trainingRoomGroup.blueprintMediaScale = this._backgroundLayer.scaleX() + ZOOM_STEP;
		this._backgroundLayer.scale({
			x: trainingRoomGroup.blueprintMediaScale,
			y: trainingRoomGroup.blueprintMediaScale,
		});
		await this._simTrainingRoomService.updateTrainingRoomGroup(trainingRoomGroup);
	}

	async zoomOutBackground(trainingRoomGroup: typeof this.trainingRoomGroup) {
		trainingRoomGroup.blueprintMediaScale = this._backgroundLayer.scaleX() - ZOOM_STEP;
		this._backgroundLayer.scale({
			x: trainingRoomGroup.blueprintMediaScale,
			y: trainingRoomGroup.blueprintMediaScale,
		});
		await this._simTrainingRoomService.updateTrainingRoomGroup(trainingRoomGroup);
	}

	uploadBackgroundImageClicked(trainingRoomGroup: typeof this.trainingRoomGroup) {
		let input = document.createElement("input") as HTMLInputElement;
		input.type = "file";
		input.accept = "image/png, image/gif, image/jpeg";
		input.onchange = (_) => {
			// you can use this method to get file and perform respective operations
			let files = input.files;
			if (files?.length > 0) {
				this._simUploadService
					.uploadBlueprintForTrainingRoomGroup({
						trainingRoomGroupId: trainingRoomGroup.id,
						uploadedFile: files[0],
					})
					.subscribe({
						next: (data) => {
							//TODO: upload progress/fehlerbehandlung, wie bei Medien
							if (data.type == HttpEventType.Response) {
								if (data.body.success) {
									SimUtilityService.copy(data.body.trainingRoomGroup, trainingRoomGroup);
									this.loadBackgroundImage(trainingRoomGroup);
								} else {
									console.warn(data.body.reason);
								}
							}
						},
					});
			}
		};
		input.click();
	}

	private loadBackgroundImage(trainingRoomGroup: typeof this.trainingRoomGroup) {
		this._backgroundLayer.destroyChildren();
		if (trainingRoomGroup.blueprintMediaId) {
			const url = this._simStreamService.getMedia_Url(trainingRoomGroup.blueprintMediaId);
			konva.Image.fromURL(url, (img) => {
				if (this.readOnly) {
					const scale = this.getScaleToFitAllTrainingRooms(trainingRoomGroup);
					this._stage.scaleX(scale);
					this._stage.scaleY(scale);
				}

				this._backgroundLayer.scaleX(trainingRoomGroup.blueprintMediaScale);
				this._backgroundLayer.scaleY(trainingRoomGroup.blueprintMediaScale);

				img.opacity(0.9);
				this._backgroundLayer.add(img);
			});
		}
	}

	/**
	 * Calculates the scale needed to fit all training rooms into the canvas.
	 */
	private getScaleToFitAllTrainingRooms(trainingRoomGroup: typeof this.trainingRoomGroup) {
		// get the minimum and maximum x and y coordinates of all training rooms, including the bounds of the viewport
		// the bounds of the viewport are included because the rooms might actually be inside the viewport, then we do not need to scale
		const trainingRoomXPositions = trainingRoomGroup.trainingRooms.map((room) => room.positionX);
		const trainingRoomYPositions = trainingRoomGroup.trainingRooms.map((room) => room.positionY);
		const minX = Math.min(0, ...trainingRoomXPositions);
		const minY = Math.min(0, ...trainingRoomYPositions);
		const maxX = Math.max(this._canvasSize.width, ...trainingRoomXPositions);
		const maxY = Math.max(this._canvasSize.height, ...trainingRoomYPositions);

		// calculate the bounding box encompassing all training rooms, scaled by 10% to add some padding
		const boundingBox = {
			width: (maxX + BUTTON_WIDTH - minX) * 1.1,
			height: (maxY + BUTTON_HEIGHT - minY) * 1.1,
		};

		// calculate the scale needed to fit the bounding box into the canvas
		// the minimum of the two scales is used to ensure that the bounding box fits into the canvas in both dimensions
		// a smaller scale means to zoom out more
		const scaleToFitWidth = this._canvasSize.width / boundingBox.width;
		const scaleToFitHeight = this._canvasSize.height / boundingBox.height;
		return Math.min(scaleToFitWidth, scaleToFitHeight);
	}

	async resetRoomPositions(
		trainingRoomGroup: {
			trainingRooms?: Array<{selectedPlayer?: PlayerMapping} & TrainingRoom>;
		} & TrainingRoomGroup,
	) {
		const tasks: Promise<unknown>[] = [];

		for (let i = 0; i < trainingRoomGroup.trainingRooms.length; i++) {
			const trainingRoom = trainingRoomGroup.trainingRooms[i];
			const group = this._layer.findOne("#" + trainingRoom.id) as konva.Group;
			group.x(10);
			group.y(10 + 10 * i + BUTTON_HEIGHT * i);

			trainingRoom.positionX = 10;
			trainingRoom.positionY = 10 + 10 * i + BUTTON_HEIGHT * i;
			tasks.push(
				this._simTrainingRoomService.updateTrainingRoom({
					trainingRoom,
					updateLayouts: false,
				}),
			);
		}

		await Promise.all(tasks);
	}
}
